import { FC, Fragment, useCallback } from "react";
import {
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";
import moment from "moment";
import { BudgetDateSelection } from "./BudgetDateSelection";
import { budgetPeriods } from "../../utils/constants/date-selection";
import { BudgetPeriod, BudgetType, Threshold } from "@/services/cloudchipr.api";
import { capitalizeString } from "@/utils/helpers/capitalizeString";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { isCurrentBudgetEditingSelector } from "@/store/budgets/selectors/current-budget/data-selectors/isCurrentBudgetEditingSelector";
import { setCurrentBudgetData } from "@/store/budgets/budgetsSlice";
import { getResourceExplorerDataByBudgetThunk } from "@/store/budgets/thunks/getResourceExplorerDataByBudgetThunk";
import { formatDate } from "@/utils/helpers/date-time/datetime-format";
import { getFQuarterByStartMonth } from "@/components/common/date-range-picker/utils/preDefinedRanges/getQuarterlyPreDefinedRange";
import { organisationQuarterStartMonthSelector } from "@/store/common/selectors/org-current-settings/organisationQuarterStartMonthSelector";

interface PeriodAndDateSelectionProps {
  period: BudgetPeriod;
  startDate: string;
  endDate?: string;
  noEndDate?: boolean;
  setFieldValue(key: string, val: string): void;
  thresholds: Threshold[];
  onThresholdChange(value: Threshold[]): void;
  onTypeChange(type: BudgetType): void;
  onPlannedAmountChange(value: (number | undefined)[] | null): void;
}

export const PeriodAndDateSelection: FC<PeriodAndDateSelectionProps> = ({
  period,
  startDate,
  endDate,
  noEndDate,
  setFieldValue,
  thresholds,
  onThresholdChange,
  onTypeChange,
  onPlannedAmountChange,
}) => {
  const dispatch = useAppDispatch();
  const isEdit = useAppSelector(isCurrentBudgetEditingSelector);
  const quarterStartMonth = useAppSelector(
    organisationQuarterStartMonthSelector,
  );

  const startDateChangeHandler = useCallback(
    (startDate: string, p?: BudgetPeriod) => {
      let dateObj = moment(startDate);
      const periodIsDaily = p === "daily" || period === "daily";
      const periodIsCustom = p === "custom" || period === "custom";

      if (!periodIsDaily && !periodIsCustom) {
        dateObj = dateObj.startOf("month");
      }

      if (periodIsCustom) {
        if (!endDate || moment(startDate).isAfter(moment(endDate))) {
          setFieldValue("endDate", startDate);
          dispatch(setCurrentBudgetData({ end_date: startDate }));
        }
      }

      const date = formatDate(dateObj);

      setFieldValue("startDate", date);
      dispatch(setCurrentBudgetData({ start_date: date }));
    },
    [setFieldValue, dispatch, period, endDate],
  );

  const endDateChangeHandler = useCallback(
    (endDate: string, p?: BudgetPeriod) => {
      let dateObj = moment(endDate);
      const periodIsDaily = p === "daily" || period === "daily";
      const periodIsCustom = p === "custom" || period === "custom";

      if (!periodIsDaily && !periodIsCustom) {
        dateObj = dateObj.startOf("month");
      }

      const date = formatDate(dateObj);

      setFieldValue("endDate", date);
      dispatch(setCurrentBudgetData({ end_date: date }));
    },
    [setFieldValue, dispatch, period],
  );

  const periodChangeHandler = useCallback(
    (event: any) => {
      const period = event.target.value;
      setFieldValue("period", period);
      dispatch(setCurrentBudgetData({ period }));

      let startDate = moment().format();

      if (period === "quarterly") {
        const quarterStart = getFQuarterByStartMonth(
          moment().month() + 1,
          quarterStartMonth,
        )?.start;
        startDate = moment(quarterStart).year(moment().year()).format();
      }

      if (unsupportedBudgetPeriods.includes(period)) {
        const updatedThresholds = thresholds.map(
          (threshold): Threshold => ({
            ...threshold,
            trigger: defaultTriggerValue,
          }),
        );
        onThresholdChange(updatedThresholds);
        onTypeChange(defaultTypeValue);
        onPlannedAmountChange(null);
      } else {
        let plannedAmounts: undefined[] = [];

        if (period === "monthly") {
          plannedAmounts = Array(12).fill(undefined);
          onPlannedAmountChange(plannedAmounts);
          dispatch(setCurrentBudgetData({ amount: undefined }));
        }

        if (period === "quarterly") {
          plannedAmounts = Array(4).fill(undefined);
          onPlannedAmountChange(plannedAmounts);
          dispatch(setCurrentBudgetData({ amount: undefined }));
        }
      }

      dispatch(getResourceExplorerDataByBudgetThunk());

      startDateChangeHandler(startDate, period);
    },
    [
      dispatch,
      startDateChangeHandler,
      setFieldValue,
      thresholds,
      onThresholdChange,
      onTypeChange,
      onPlannedAmountChange,
      quarterStartMonth,
    ],
  );

  const noEndDateChangeHandler = useCallback(
    (event: any) => {
      const noEndDate = event.target.checked;
      setFieldValue("noEndDate", noEndDate);
      dispatch(setCurrentBudgetData({ no_end_date: noEndDate }));
    },
    [setFieldValue, dispatch],
  );

  return (
    <Fragment>
      <FormControl size="small">
        <InputLabel>Period</InputLabel>
        <Select
          fullWidth
          value={period}
          disabled={isEdit}
          onChange={periodChangeHandler}
          renderValue={(val) => capitalizeString(val)}
          label="Period"
        >
          {budgetPeriods.map((period) => {
            return (
              <MenuItem key={period} value={period}>
                <ListItemText>{capitalizeString(period)}</ListItemText>
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>

      <Stack direction="row">
        <BudgetDateSelection
          period={period}
          startDate={startDate}
          endDate={endDate}
          noEndDate={noEndDate}
          onStartDateChange={startDateChangeHandler}
          onEndDateChange={endDateChangeHandler}
          noEndDateChangeHandler={noEndDateChangeHandler}
        />
      </Stack>
    </Fragment>
  );
};

const unsupportedBudgetPeriods = ["daily", "annually", "custom"];
const defaultTriggerValue = "actual";
const defaultTypeValue = "fixed";
